import type { PaxType, Passenger, PaxDocument } from '@optionway/models';
import { modelsImpl, PaxDocumentTypeEnum, PaxTypeEnum } from '@optionway/models';

interface PassengersStore {
  // Internal cache of the passengers, keyed by id — read `passengers`, never this. An object is created
  // once and then only modified: the forms bind to it, so what the traveller typed survives a count change.
  byKey: Record<string, Passenger>;
}

export const usePassengersStore = defineStore('passengers', {
  state: (): PassengersStore => ({ byKey: {} }),
  getters: {
    // The shape of the list: blank passengers for the COMMITTED criteria — the live form must not rebuild
    // it at every keystroke. Never bound by a form: the forms bind to the cache, filled from this shape.
    committedPassengers(): Passenger[] {
      const { adults, children, infants } = useSearchCriteriaStore().committedCriteriaKey;
      return buildPassengers(adults, children, infants);
    },
    // The public list: the shape, served from the cache. Empty until the first syncWithCriteria, which
    // applyPricing runs at every entry into the funnel — the only place this list is read.
    passengers(): Passenger[] {
      return this.committedPassengers.flatMap((blank) => {
        const passenger = this.byKey[blank.id];
        return passenger ? [passenger] : [];
      });
    },
    documentRequired(): boolean {
      return useSelectedOfferStore().offer?.flight?.documentRequired ?? false;
    },
    // Partial for real: a type with no passenger has no key — a booking without children has no CHILD group.
    groupedByType(): Partial<Record<PaxType, Passenger[]>> {
      return Object.groupBy(this.passengers, (pax) => pax.type);
    },
    hasInfant(): boolean {
      return this.passengers.some((passenger) => passenger.type === 'INFANT');
    },
    completedCount(): (now: string) => number {
      const lastFlightDate = useSearchCriteriaStore().lastFlightDate;
      return (now: string) =>
        this.passengers.filter((passenger) =>
          isPassengerComplete(passenger, this.documentRequired, lastFlightDate, now),
        ).length;
    },
    incompleteCount(): (now: string) => number {
      const completedCount = this.completedCount;
      return (now: string) => this.passengers.length - completedCount(now);
    },
    firstIncompleteIndex(): (now: string) => number {
      const lastFlightDate = useSearchCriteriaStore().lastFlightDate;
      return (now: string) =>
        this.passengers.findIndex(
          (passenger) => !isPassengerComplete(passenger, this.documentRequired, lastFlightDate, now),
        );
    },
    mainPassenger(): Passenger | undefined {
      return this.passengers[0];
    },
    // Memoized index lookup so repeated per-row template calls don't rescan the array.
    flatIndexOf(): (passenger: Passenger) => number {
      const indexes = new Map(this.passengers.map((passenger, index) => [passenger, index]));
      return (passenger: Passenger) => indexes.get(passenger) ?? -1;
    },
    // Position of a passenger within its type, from 1 — the "Adult 1 / Child 1" the pages display.
    // A fact about the list, memoized like flatIndexOf; 0 for a passenger the list does not hold.
    ordinalInType(): (passenger: Passenger) => number {
      const ordinals = new Map<Passenger, number>();
      for (const group of Object.values(this.groupedByType)) {
        for (const [index, passenger] of group.entries()) {
          ordinals.set(passenger, index + 1);
        }
      }
      return (passenger: Passenger) => ordinals.get(passenger) ?? 0;
    },
  },
  actions: {
    // Reconciles the cache with the shape: existing passengers kept as they are, new keys adopt their
    // blank, keys out of the shape dropped, the main passenger alone gets a contact. Called by applyPricing.
    syncWithCriteria() {
      const next: Record<string, Passenger> = {};
      for (const blank of this.committedPassengers) {
        next[blank.id] = this.byKey[blank.id] ?? blank;
      }
      this.byKey = next;
      const first = this.passengers[0];
      if (first && !first.contact) {
        first.contact = toPojo(new modelsImpl.ContactImpl());
      }
    },
    // Ensures passenger.documents[0] exists so fields can bind straight to it, mirroring the contact
    // step of syncWithCriteria — returns the existing document if already present.
    ensureDocument(passenger: Passenger): PaxDocument {
      const existing = passenger.documents?.[0];
      if (existing) {
        return existing;
      }
      const document = toPojo(new modelsImpl.PaxDocumentImpl());
      document.type = PaxDocumentTypeEnum.PASSEPORT;
      passenger.documents = [document];
      return document;
    },
  },
});

// Adults, then children, then infants, numbered per type from 1 — the order the pages display.
function buildPassengers(adults: number, children: number, infants: number): Passenger[] {
  const counts: [PaxType, number][] = [
    [PaxTypeEnum.ADULT, adults],
    [PaxTypeEnum.CHILD, children],
    [PaxTypeEnum.INFANT, infants],
  ];
  return counts.flatMap(([type, count]) =>
    Array.from({ length: count }, (_, index) => createPassenger(type, index + 1)),
  );
}

// The id is the passenger's identity: type and position within the type (`ADULT-1`, `CHILD-1`). Numbering
// per type keeps it stable when another type's count changes — adding an adult leaves CHILD-1 untouched.
// toPojo: only serializable data may enter the store state — a class instance in the SSR payload
// makes devalue throw and fails the whole server render (same pattern as the other stores).
function createPassenger(paxType: PaxType, ordinal: number): Passenger {
  const passenger = toPojo(new modelsImpl.PassengerImpl());
  passenger.id = `${paxType}-${ordinal}`;
  passenger.type = paxType;
  passenger.contact = undefined;
  return passenger;
}
